<script lang="ts">
	// The AI summaries, as "Home Visibility" draws them. Not built yet; shown
	// without «قريبًا» at the owner's decision (2026-10-03). The summary is an
	// example and says so.

	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Eyebrow from '../components/Eyebrow.svelte';
	import Loading from '../components/Loading.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { summaries, type Cadence } from './copy';
	import Rich from './Rich.svelte';

	let cadence = $state<Cadence>('daily');

	const summary = $derived(summaries.byCadence[cadence]);
	const options = summaries.options.map((option) => ({
		...option,
		testId: `site-summary-${option.value}`,
	}));
</script>

<section id="summaries" class="section">
	<div class="split">
		<div>
			<div class="kicker">
				<Eyebrow tone="speed" text={summaries.eyebrow} />
				<span class="caption">{summaries.example}</span>
			</div>
			<h2 class="h2">{summaries.title}</h2>
			<p class="copy intro">{summaries.body}</p>
			<div class="cadence">
				<Segmented
					label={summaries.label}
					{options}
					value={cadence}
					onChange={(value) => (cadence = value as Cadence)}
					testId="site-summary-cadence"
				/>
			</div>
			<div class="channels">
				<span class="caption">{summaries.via}</span>
				{#each summaries.channels as channel (channel)}
					<Chip tone="neutral" size="md" text={channel} />
				{/each}
			</div>
		</div>

		<Card testId="site-summary">
			<div class="head">
				<div class="title">
					<Loading size="sm" state="found" label={summaries.mark} />
					<span>{summary.title}</span>
				</div>
				<span class="figure time">{summary.time}</span>
			</div>
			<div class="body">
				{#each summary.lines as line (line.text)}
					<div class="line">
						<span class="dot" style:background={line.dot}></span>
						<p><Rich line={line.text} /></p>
					</div>
				{/each}
				<div class="actions">
					<Button
						testId="site-summary-ask"
						tone="secondary"
						size="sm"
						label={summary.ask}
						href="#ask"
					/>
					<Button
						testId="site-summary-report"
						tone="text"
						size="sm"
						label={summaries.openReport}
						href="#reports"
					/>
				</div>
			</div>
		</Card>
	</div>
</section>

<style>
	.split {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
		gap: var(--space-12);
		align-items: start;
	}

	.intro {
		margin-block-end: var(--space-9);
	}

	/* The control takes its options' width, as the design sizes it. */
	.cadence {
		display: flex;
	}

	.channels {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
		align-items: center;
		margin-block-start: var(--space-9);
	}

	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--space-5);
		padding: var(--panel-header-pad-lg);
		background: var(--surface-card-header);
		border-block-end: var(--border-width) solid var(--border-default);
	}

	.title {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		color: var(--text-strong);
		font-size: var(--text-base);
		font-weight: var(--weight-semibold);
	}

	.time {
		font-size: var(--text-micro);
		color: var(--text-muted);
	}

	.body {
		display: flex;
		flex-direction: column;
		gap: var(--space-6);
		padding: var(--space-9);
	}

	.line {
		display: grid;
		grid-template-columns: 7px minmax(0, 1fr);
		gap: var(--space-5);
		align-items: baseline;
	}

	.dot {
		inline-size: 7px;
		block-size: 7px;
		border-radius: var(--radius-pill);
		transform: translateY(-2px);
	}

	.line p {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-body);
		color: var(--text-body);
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
		padding-block-start: var(--space-6);
		border-block-start: var(--border-width) solid var(--border-inset);
	}
</style>
